<script setup>
import { deleteRequest, getRequest } from "@/utils/request.js";
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import PermissionFormModal from "./modules/PermissionFormModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const columns = [
  {
    key: "name",
    dataIndex: "name",
    title: "菜单名称",
    width: "15%",
  },
  {
    key: "path",
    dataIndex: "path",
    title: "路径",
    width: "10%",
  },
  {
    key: "component",
    dataIndex: "component",
    title: "组件",
    width: "10%",
  },
  {
    key: "componentName",
    dataIndex: "componentName",
    title: "组件名称",
    width: "10%",
  },
  {
    key: "type",
    dataIndex: "type",
    title: "类型",
    width: "5%",
  },
  {
    key: "perms",
    dataIndex: "perms",
    title: "权限编码",
    width: "10%",
  },
  {
    key: "sort",
    dataIndex: "sort",
    title: "排序",
    width: "3%",
  },
  {
    key: "icon",
    dataIndex: "icon",
    title: "图标",
    width: "5%",
  },
  {
    key: "keepAlive",
    dataIndex: "keepAlive",
    title: "是否缓存",
    width: "5%",
  },
  {
    key: "hidden",
    dataIndex: "hidden",
    title: "是否隐藏",
    width: "5%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/permission/treeList", requestBody)
    .then((response) => {
      if (response.success) {
        datasource.value = response.data;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAdd = (record) => {
  modalRef.value.title = "菜单/权限新增";
  modalRef.value.handleAdd(record);
};

const handleEdit = (record) => {
  modalRef.value.title = "菜单/权限编辑";
  modalRef.value.handleEdit(record);
};

const handleDelete = (record) => {
  deleteRequest(`/permission/${record.id}`).then((response) => {
    if (response.success) {
      message.success("删除成功");
      loadData();
    } else {
      message.error("删除失败。" + response.message);
    }
  });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="菜单名称" name="name">
              <a-input
                v-model:value="searchFormModel.name"
                placeholder="请输入菜单名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="路径" name="path">
              <a-input
                v-model:value="searchFormModel.path"
                placeholder="请输入路径"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="组件" name="component">
              <a-input
                v-model:value="searchFormModel.component"
                placeholder="请输入组件"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="权限编码" name="perms">
              <a-input
                v-model:value="searchFormModel.perms"
                placeholder="请输入权限编码"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px">
        <a-button
          v-has="'perm:add'"
          type="primary"
          @click="handleAdd"
          :icon="h(PlusOutlined)"
          >新增
        </a-button>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="false"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'icon'"></template>
          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green">启用</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">停用</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'type'">
            <template v-if="record[column.dataIndex] === 0">
              <a-tag color="green">一级菜单</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 1">
              <a-tag color="green">子菜单</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 2">
              <a-tag color="green">按钮/接口权限</a-tag>
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'keepAlive'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green">是</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">否</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'hidden'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green">是</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">否</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a v-has="'perm:update'" type="link" @click="handleEdit(record)"
              >编辑</a
            >
            <a-divider type="vertical" />
            <a-popconfirm
              v-has="'perm:delete'"
              title="确认要删除吗?"
              @confirm="handleDelete(record)"
            >
              <a v-has="'perm:delete'" type="link">删除</a>
            </a-popconfirm>
            <a-divider type="vertical" />
            <a
              v-has="'perm:add'"
              type="link"
              @click="handleAdd({ parentId: record.id })"
              >增加下级菜单</a
            >
          </template>
        </template>
      </a-table>
    </a-card>
    <!--    <role-form-modal ref="modalRef" @ok="loadData" />-->
    <permission-form-modal ref="modalRef" @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
